"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Info, Layers, ShieldCheck, SlidersHorizontal, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
export default function Rigaku() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("all");
  const products = [{
    id: "progeny",
    title: "Rigaku Progeny 1064nm",
    family: "progeny",
    tagline: "Regulated Raw Material Identification",
    badge: "Pharma / GMP Standard",
    description: "Engineered for high-throughput raw material verification directly through transparent packaging, minimizing fluorescence interference.",
    primaryApplication: "Raw Material ID (RMID) & Finished Product QC",
    laser: "1064 nm Nd:YAG Laser",
    spectralRange: "200 - 2,500 cm⁻¹",
    compliance: "Electronic Records & Audit Trail Support",
    protection: "IP68 Submersible Design",
    reviewStatus: "Specs marked for qualification review",
    link: "/RawMaterialIdentificationPage"
  }, {
    id: "progeny-chem",
    title: "Rigaku Progeny Chem",
    family: "progeny",
    tagline: "Chemical & Polymer Verification",
    badge: "Chemical & Industrial",
    description: "Tailored for non-destructive chemical formulation analysis, polymer sorting, and industrial testing with extensive compound libraries.",
    primaryApplication: "Specialty Chemicals & Polymers",
    laser: "1064 nm Nd:YAG Laser",
    spectralRange: "200 - 2,500 cm⁻¹",
    compliance: "Factory Calibrated / Data Integrity",
    protection: "IP67 Ruggedized Enclosure",
    reviewStatus: "Specs marked for qualification review",
    link: "/ChemicalManufacturingPage"
  }, {
    id: "resq-cql",
    title: "Rigaku CQL / ResQ CQL",
    family: "cql",
    tagline: "Field Chemical Threat & Narcotics ID",
    badge: "Safety & Defence",
    description: "Handheld Raman analyzer built for first responders, border security, and defence squads to detect bulk narcotics, explosives, and precursor agents.",
    primaryApplication: "Narcotics, HazMat & Explosives Identification",
    laser: "1064 nm Laser with Multi-Mode",
    spectralRange: "150 - 2,800 cm⁻¹",
    compliance: "MIL-STD-810G & QuickDetect Mode",
    protection: "MIL-STD-810G / IP68 Rated",
    reviewStatus: "Specs marked for qualification review",
    link: "/SafetyAndSecurityPage"
  }, {
    id: "cql-gen-id",
    title: "Rigaku CQL Gen-ID",
    family: "cql",
    tagline: "Tactical Threat Detection & Forensics",
    badge: "Tactical Security",
    description: "Enhanced spectral intelligence for trace detection, dirty mixtures, cutting agents, and emerging synthetic threats directly in the field.",
    primaryApplication: "Forensics, Illicit Drugs & Hazmat Analysis",
    laser: "1064 nm High-Resolution Raman",
    spectralRange: "100 - 2,850 cm⁻¹",
    compliance: "SWGDRUG Category A Compliant",
    protection: "Decontamination Ready / IP68",
    reviewStatus: "Specs marked for qualification review",
    link: "/BorderSecurityAndDefencePage"
  }];
  const filteredProducts = activeTab === "all" ? products : products.filter(p => p.family === activeTab);
  const specRows = [{
    spec: "Excitation Laser",
    progeny: "1064 nm (Fluorescence Mitigation)",
    cql: "1064 nm (Fluorescence Mitigation)",
    note: "Standard 1064nm platform across all systems"
  }, {
    spec: "Optical Sampling Geometry",
    progeny: "Adjustable focal point for packaging",
    cql: "Direct contact, vial, and standoff adaptors",
    note: "Neutral baseline pending application test"
  }, {
    spec: "Regulatory & Data Integrity",
    progeny: "Configurable audit trail, user access controls",
    cql: "Secure chain-of-custody, GPS tagging",
    note: "IQ/OQ/PQ documentation supported by Antech"
  }, {
    spec: "Ingress & Ruggedness",
    progeny: "IP68 sealed, washdown compliant",
    cql: "MIL-STD-810G drop and water tested",
    note: "Built for laboratory and field deployment"
  }, {
    spec: "Library Capabilities",
    progeny: "Pharma, Excipients, USP/EP databases",
    cql: "TIC/TIM, Narcotics, CWA, Explosives",
    note: "Custom user library addition supported"
  }];
  return <section id="rigakuproductfamilygrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-3xl space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono text-xs uppercase tracking-wide">
                Rigaku Analytical Portfolio
              </Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center text-muted-foreground cursor-help text-xs">
                      <Info className="size-3.5 text-muted-foreground mr-1" />
                      Review Notice
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Specifications listed are standard baseline representations. Antech Solutions provides application-specific qualification for Irish and UK teams.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
              Rigaku Progeny & CQL Family Systems
            </h2>
            <p className="text-base md:text-lg text-muted-foreground">
              Handheld 1064nm Raman analyzers built for rapid non-destructive identification in regulated pharmaceutical workflows, chemical manufacturing, and field defence operations.
            </p>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-2 bg-secondary/80 p-1.5 rounded-lg border border-border">
            <Button variant={activeTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("all")} className="text-xs">
              All Systems ({products.length})
            </Button>
            <Button variant={activeTab === "progeny" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("progeny")} className="text-xs">
              Progeny Series
            </Button>
            <Button variant={activeTab === "cql" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("cql")} className="text-xs">
              CQL / ResQ Series
            </Button>
          </div>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {filteredProducts.map((product, i) => <motion.div key={product.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="border-border text-foreground font-medium">
                      {product.badge}
                    </Badge>
                    <span className="flex items-center text-xs font-mono text-muted-foreground gap-1">
                      <AlertCircle className="size-3 text-primary" />
                      {product.reviewStatus}
                    </span>
                  </div>
                  <div>
                    <CardTitle className="text-xl md:text-2xl font-bold font-default text-card-foreground">
                      {product.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-primary mt-1">
                      {product.tagline}
                    </CardDescription>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {product.description}
                  </p>
                </CardHeader>

                <CardContent className="space-y-4">
                  <Separator />
                  
                  {/* Technical Spec Matrix */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="bg-secondary/60 p-3 rounded-md border border-border/50">
                      <span className="text-muted-foreground block font-mono uppercase">Excitation Laser</span>
                      <span className="font-semibold text-foreground mt-0.5 block">{product.laser}</span>
                    </div>
                    <div className="bg-secondary/60 p-3 rounded-md border border-border/50">
                      <span className="text-muted-foreground block font-mono uppercase">Spectral Range</span>
                      <span className="font-semibold text-foreground mt-0.5 block">{product.spectralRange}</span>
                    </div>
                    <div className="bg-secondary/60 p-3 rounded-md border border-border/50">
                      <span className="text-muted-foreground block font-mono uppercase">Compliance & Standard</span>
                      <span className="font-semibold text-foreground mt-0.5 block">{product.compliance}</span>
                    </div>
                    <div className="bg-secondary/60 p-3 rounded-md border border-border/50">
                      <span className="text-muted-foreground block font-mono uppercase">Ingress & Durability</span>
                      <span className="font-semibold text-foreground mt-0.5 block">{product.protection}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                    <span>Primary Target: <strong className="text-foreground">{product.primaryApplication}</strong></span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2 border-t border-border flex items-center justify-between gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={product.link} className="flex items-center gap-1.5">
                      <FileText className="size-3.5" />
                      View Application Details
                    </Link>
                  </Button>
                  <Button variant="default" size="sm" asChild>
                    <Link to="/ContactPage" className="flex items-center gap-1.5">
                      Request Technical Spec
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Neutral Comparison Matrix (Marked for Technical Review) */}
        <div className="rounded-xl border border-border bg-card p-6 md:p-8 space-y-6 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="size-4 text-primary" />
                <h3 className="text-lg md:text-xl font-bold font-default text-card-foreground">
                  Platform Comparison & Verification Matrix
                </h3>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground">
                Neutral technical benchmark parameters. Subject to Antech Solutions qualification and sample testing.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs font-mono">
                Revision: Verified 2025
              </Badge>
            </div>
          </div>

          <div className="border border-border rounded-lg overflow-hidden bg-background">
            <Table>
              <TableHeader className="bg-muted/60">
                <TableRow>
                  <TableHead className="w-[200px] font-bold text-foreground">Technical Criterion</TableHead>
                  <TableHead className="font-bold text-foreground">Rigaku Progeny Series</TableHead>
                  <TableHead className="font-bold text-foreground">Rigaku CQL / ResQ Series</TableHead>
                  <TableHead className="text-right font-bold text-foreground">Qualification Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {specRows.map((row, idx) => <TableRow key={idx} data-index={idx}>
                    <TableCell className="font-medium text-foreground text-xs md:text-sm">
                      {row.spec}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm text-muted-foreground">
                      {row.progeny}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm text-muted-foreground">
                      {row.cql}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground font-mono">
                      {row.note}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {/* Technical Review Summary Banner */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-lg bg-secondary/50 border border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-background border border-border text-primary shrink-0">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Need direct sample feasibility or method development?
                </p>
                <p className="text-xs text-muted-foreground">
                  Antech Solutions offers sample screening, IQ/OQ qualification protocols, and onsite demonstrations across Ireland and the UK.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="default" size="sm" asChild className="w-full sm:w-auto">
                <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                  <Sparkles className="size-3.5" />
                  Book Laboratory Trial
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}